import React from "react";
import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import { FavoriteBorderRounded, FavoriteRounded } from "@mui/icons-material";

import SideDrawer from "../ui/SideDrawer";
import { HexTile } from "../ui/Hexagon";
import StatusBadge from "../ui/StatusBadge";
import { getPeriodLabel } from "../../utils/period-label";
import { Details } from "../../pages/adm/oral/OralDetail/oral-detail";

type Props = {
    open: boolean;
    onClose: () => void;
    detail: Details;
    onFavorite?: (id: number, favorite: boolean) => void;
};

function SectionLabel({ children }: { children: string }) {
    return (
        <Typography sx={{ mb: 1, fontSize: "11px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "primary.main" }}>
            {children}
        </Typography>
    );
}

function SectionText({ children }: { children: React.ReactNode }) {
    return <Typography sx={{ fontSize: "14px", lineHeight: 1.7, color: "text.secondary" }}>{children}</Typography>;
}

export const OralDetailDrawer: React.FC<Props> = ({ open, onClose, detail, onFavorite }) => {
    const theme = useTheme();

    const unit = detail.unity ?? "";
    const conc = [detail.min, detail.max].filter((v) => v != null && `${v}` !== "").join(" – ");
    const period = getPeriodLabel(detail.period);
    const tags = [...(detail.classifications || []), ...(detail.userClassifications || [])];

    return (
        <SideDrawer
            open={open}
            onClose={onClose}
            header={
                <Box sx={{ width: "100%", minWidth: 0 }}>
                    <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start", width: "100%", minWidth: 0 }}>
                        <Box sx={{ flexShrink: 0 }}>
                            <HexTile label={detail.name ?? ""} size={48} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                            <Typography
                                sx={{
                                    fontFamily: theme.typography.fontFamilyDisplay,
                                    fontSize: { xs: 22, sm: 25 },
                                    lineHeight: 1.15,
                                    color: "text.primary",
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 3,
                                    overflow: "hidden",
                                    overflowWrap: "break-word",
                                    wordBreak: "break-word",
                                }}
                            >
                                {detail.name}
                            </Typography>
                            <Box sx={{ mt: "6px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                <StatusBadge status="Oral" tone="neutral" />
                            </Box>
                        </Box>
                    </Box>
                    {!!onFavorite && (
                        <Box sx={{ mt: "18px", display: "flex", gap: 1.25 }}>
                            <Button
                                fullWidth
                                onClick={() => onFavorite(detail.id ?? 0, !!detail.favorite)}
                                startIcon={detail.favorite ? <FavoriteRounded sx={{ color: "primary.main" }} /> : <FavoriteBorderRounded />}
                                sx={{ height: 42, border: `1.5px solid ${theme.palette.divider}`, color: "secondary.main" }}
                            >
                                {detail.favorite ? "Favorito" : "Favoritar"}
                            </Button>
                        </Box>
                    )}
                </Box>
            }
            footer={
                <Button fullWidth variant="outlined" color="secondary" onClick={onClose} sx={{ height: 48 }}>
                    Fechar
                </Button>
            }
        >
            {detail.mechanism && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Mecanismo de ação</SectionLabel>
                    <SectionText>{detail.mechanism}</SectionText>
                </Box>
            )}
            {detail.description && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Descrição</SectionLabel>
                    <SectionText>{detail.description}</SectionText>
                </Box>
            )}

            {conc && (
                <Box sx={{ mb: "22px" }}>
                    <Box sx={{ backgroundColor: theme.palette.brand.surface2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: "14px 16px" }}>
                        <Typography sx={{ mb: "5px", fontSize: "10.5px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "text.secondary" }}>
                            Dosagem usual
                        </Typography>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "primary.main" }}>{`${conc} ${unit}`}</Typography>
                    </Box>
                </Box>
            )}

            {period && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Período</SectionLabel>
                    <SectionText>{period}</SectionText>
                </Box>
            )}

            {tags.length > 0 && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Classificações</SectionLabel>
                    <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {tags.map((tag, idx) => (
                            <Chip key={`${tag}-${idx}`} label={tag} size="small" sx={{ backgroundColor: theme.palette.brand.cream, color: "primary.dark", fontWeight: 600 }} />
                        ))}
                    </Box>
                </Box>
            )}

            {detail.source && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Fonte</SectionLabel>
                    <SectionText>{detail.source}</SectionText>
                </Box>
            )}

            {detail.annotation && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Minha anotação</SectionLabel>
                    <SectionText>{detail.annotation}</SectionText>
                </Box>
            )}
        </SideDrawer>
    );
};

export default OralDetailDrawer;
